import * as THREE from 'three';
import type { World } from '../game/World.ts';

export type ShrineKind = 'arcane' | 'cursed' | 'obelisk' | 'anvil';

export const SHRINE_SPAWN_TIMES: readonly number[] = [45, 165, 285];
export const ANVIL_SPAWN_TIMES: readonly number[] = [105, 225];
export const SHRINE_RADIUS = 2.6;
export const SHRINE_CHANNEL_TIME = 2.0;

export const SHRINE_COLORS: Record<ShrineKind, string> = {
  arcane: '#5fc8ff',
  cursed: '#ff4268',
  obelisk: '#ffd24d',
  anvil: '#d48cff',
};

export interface ShrineData {
  kind: ShrineKind;
  x: number;
  z: number;
  progress: number;
  active: boolean;
}

export interface ShrineRewardOut {
  heal: number;
  buffMight: number;
  buffHaste: number;
  buffDuration: number;
  selfDamage: number;
  spawnElites: number;
  dropChest: boolean;
  gold: number;
  vacuumGems: boolean;
  spendGold?: number;
  upgradeRandomSpell?: boolean;
}

export function stepShrineChannel(shrine: ShrineData, px: number, pz: number, dt: number): boolean {
  if (!shrine.active) return false;
  const dist = Math.hypot(px - shrine.x, pz - shrine.z);
  if (dist <= SHRINE_RADIUS) {
    shrine.progress = Math.min(1, shrine.progress + dt / SHRINE_CHANNEL_TIME);
    if (shrine.progress >= 1) {
      shrine.active = false;
      return true;
    }
  } else {
    shrine.progress = Math.max(0, shrine.progress - dt * 0.5);
  }
  return false;
}

export function applyShrineReward(kind: ShrineKind, playerHp: number, goldRun = 0): ShrineRewardOut {
  switch (kind) {
    case 'arcane':
      return {
        heal: 35,
        buffMight: 0.35,
        buffHaste: 0.25,
        buffDuration: 20,
        selfDamage: 0,
        spawnElites: 0,
        dropChest: false,
        gold: 0,
        vacuumGems: false,
      };
    case 'cursed':
      return {
        heal: 0,
        buffMight: 0,
        buffHaste: 0,
        buffDuration: 0,
        selfDamage: Math.max(1, Math.floor(playerHp * 0.25)),
        spawnElites: 2,
        dropChest: true,
        gold: 0,
        vacuumGems: false,
      };
    case 'obelisk':
      return {
        heal: 0,
        buffMight: 0,
        buffHaste: 0,
        buffDuration: 0,
        selfDamage: 0,
        spawnElites: 0,
        dropChest: false,
        gold: 75,
        vacuumGems: true,
      };
    case 'anvil': {
      const canForge = goldRun >= 50;
      return {
        heal: 25,
        buffMight: canForge ? 0 : 0.25,
        buffHaste: canForge ? 0 : 0.15,
        buffDuration: canForge ? 0 : 15,
        selfDamage: 0,
        spawnElites: 0,
        dropChest: false,
        gold: 0,
        vacuumGems: false,
        spendGold: canForge ? 50 : 0,
        upgradeRandomSpell: canForge,
      };
    }
  }
}

interface ShrineInstance extends ShrineData {
  group: THREE.Group;
  crystal: THREE.Mesh;
  ring: THREE.Mesh;
  progressRing: THREE.Mesh;
  crystalMat: THREE.MeshStandardMaterial;
  ringMat: THREE.MeshBasicMaterial;
  progMat: THREE.MeshBasicMaterial;
}

const SHRINE_ORDER: ShrineKind[] = ['arcane', 'cursed', 'obelisk'];

export class Shrines {
  readonly root = new THREE.Group();
  private readonly instances: ShrineInstance[] = [];
  private nextSpawnIndex = 0;
  private nextAnvilIndex = 0;
  private readonly tmpColor = new THREE.Color();
  private readonly sharedGeos: THREE.BufferGeometry[] = [];
  private baseMat?: THREE.MeshStandardMaterial;
  private readonly world: World;
  private readonly onComplete: (kind: ShrineKind, x: number, z: number, reward: ShrineRewardOut) => void;

  constructor(
    world: World,
    onComplete: (kind: ShrineKind, x: number, z: number, reward: ShrineRewardOut) => void,
  ) {
    this.world = world;
    this.onComplete = onComplete;
    const baseGeo = new THREE.CylinderGeometry(0.65, 0.85, 0.55, 6);
    const crystalGeo = new THREE.OctahedronGeometry(0.62, 0);
    const ringGeo = new THREE.RingGeometry(SHRINE_RADIUS - 0.14, SHRINE_RADIUS, 32);
    ringGeo.rotateX(-Math.PI / 2);
    const progGeo = new THREE.RingGeometry(0.85, SHRINE_RADIUS - 0.18, 32);
    progGeo.rotateX(-Math.PI / 2);

    const baseMat = new THREE.MeshStandardMaterial({ color: 0x2a293e, roughness: 0.75, metalness: 0.2, flatShading: true });
    this.sharedGeos.push(baseGeo, crystalGeo, ringGeo, progGeo);
    this.baseMat = baseMat;

    for (let i = 0; i < 5; i += 1) {
      const group = new THREE.Group();
      group.visible = false;

      const base = new THREE.Mesh(baseGeo, baseMat);
      base.position.y = 0.28;
      group.add(base);

      const crystalMat = new THREE.MeshStandardMaterial({
        color: 0x5fc8ff,
        emissive: 0x5fc8ff,
        emissiveIntensity: 1.2,
        roughness: 0.25,
        metalness: 0.3,
        flatShading: true,
      });
      const crystal = new THREE.Mesh(crystalGeo, crystalMat);
      crystal.position.y = 1.45;
      group.add(crystal);

      // Flat rings seen from above: one pass draws them all. Without forceSinglePass three draws a see-through
      // double-sided material twice (back faces, then front) and rebuilds its shader lookup both times, every frame.
      const ringMat = new THREE.MeshBasicMaterial({
        color: 0x5fc8ff,
        transparent: true,
        opacity: 0.45,
        side: THREE.DoubleSide,
        forceSinglePass: true,
        depthWrite: false,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.y = 0.06;
      group.add(ring);

      const progMat = new THREE.MeshBasicMaterial({
        color: 0x5fc8ff,
        transparent: true,
        opacity: 0.28,
        side: THREE.DoubleSide,
        forceSinglePass: true,
        depthWrite: false,
      });
      const progressRing = new THREE.Mesh(progGeo, progMat);
      progressRing.position.y = 0.05;
      progressRing.scale.setScalar(0.01);
      group.add(progressRing);

      this.root.add(group);
      this.instances.push({
        kind: i < 3 ? SHRINE_ORDER[i] : 'anvil',
        x: 0,
        z: 0,
        progress: 0,
        active: false,
        group,
        crystal,
        ring,
        progressRing,
        crystalMat,
        ringMat,
        progMat,
      });
    }
  }

  reset(): void {
    this.nextSpawnIndex = 0;
    this.nextAnvilIndex = 0;
    for (const s of this.instances) {
      s.active = false;
      s.progress = 0;
      s.group.visible = false;
    }
  }

  spawnAt(index: number, kind: ShrineKind, x: number, z: number): void {
    const s = this.instances[index];
    if (!s) return;
    s.kind = kind;
    s.x = x;
    s.z = z;
    s.progress = 0;
    s.active = true;
    s.group.position.set(x, 0, z);
    s.group.visible = true;
    this.tmpColor.set(SHRINE_COLORS[kind]);
    s.crystalMat.color.copy(this.tmpColor);
    s.crystalMat.emissive.copy(this.tmpColor);
    s.ringMat.color.copy(this.tmpColor);
    s.progMat.color.copy(this.tmpColor);
    s.progressRing.scale.setScalar(0.01);
  }

  update(dt: number): void {
    const w = this.world;
    if (this.nextSpawnIndex < SHRINE_SPAWN_TIMES.length && w.time >= SHRINE_SPAWN_TIMES[this.nextSpawnIndex]) {
      const idx = this.nextSpawnIndex++;
      const kind = SHRINE_ORDER[(idx + w.stageIndex) % SHRINE_ORDER.length];
      const angle = w.rng() * Math.PI * 2;
      const dist = 16 + w.rng() * 6;
      const x = w.player.x + Math.cos(angle) * dist;
      const z = w.player.z + Math.sin(angle) * dist;
      this.spawnAt(idx, kind, x, z);
    }
    if (this.nextAnvilIndex < ANVIL_SPAWN_TIMES.length && w.time >= ANVIL_SPAWN_TIMES[this.nextAnvilIndex]) {
      const idx = 3 + this.nextAnvilIndex++;
      const angle = w.rng() * Math.PI * 2;
      const dist = 14 + w.rng() * 6;
      const x = w.player.x + Math.cos(angle) * dist;
      const z = w.player.z + Math.sin(angle) * dist;
      this.spawnAt(idx, 'anvil', x, z);
    }

    const px = w.player.x;
    const pz = w.player.z;
    for (const s of this.instances) {
      if (!s.active) continue;
      const inView = Math.abs(s.x - px) < 26 && Math.abs(s.z - pz) < 22;
      s.group.visible = inView;
      s.crystal.rotation.y += dt * 1.6;
      s.crystal.position.y = 1.45 + Math.sin(w.time * 2.8) * 0.16;
      const completed = stepShrineChannel(s, px, pz, dt);
      s.progressRing.scale.setScalar(Math.max(0.01, s.progress));
      if (completed) {
        s.group.visible = false;
        const reward = applyShrineReward(s.kind, w.player.hp);
        this.onComplete(s.kind, s.x, s.z, reward);
      }
    }
  }

  activeShrines(cb: (x: number, z: number, color: string) => void): void {
    for (const s of this.instances) {
      if (s.active) cb(s.x, s.z, SHRINE_COLORS[s.kind]);
    }
  }

  dispose(): void {
    for (const s of this.instances) {
      s.crystalMat.dispose();
      s.ringMat.dispose();
      s.progMat.dispose();
    }
    this.instances.length = 0;
    for (const g of this.sharedGeos) g.dispose();
    this.sharedGeos.length = 0;
    this.baseMat?.dispose();
  }
}
